import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	FormHelperText,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useState } from 'react';
import copy from 'copy-to-clipboard';
import ConfirmDialog from 'shared/_components/confirm-dialog';
import { IconCopy } from 'shared/_ui/icon';
import DESIGN_COLORS from 'shared/_ui/design-colors';
import { frontEndLocation } from 'shared/constants/system';
import { ROUTE } from 'shared/constants/routes_old';
import { ShareAccessType } from 'shared/_model/sharing/ShareDialog/constants';
import SettingRow from 'shared/_model/sharing/ShareDialog/setting-row';
import useShareSettings, {
	SharePermissions,
} from 'shared/_model/sharing/ShareDialog/use-share-settings';
import { generatePassword } from 'shared/_model/sharing/ShareDialog/utils';
import { SharingType } from 'shared/_model/sharing/types';

type ShareDialogProps = {
	documentId: string;
	type: SharingType;
	handleClose: () => void;
	onChange?: () => void;
};

const TITLE_MAP: Record<SharingType, string> = {
	[SharingType.PROJECT]: 'project',
	[SharingType.FILE]: 'file',
};

const PERMISSIONS: { key: keyof SharePermissions; title: string; subtitle: string }[] = [
	{ key: 'canEdit', title: 'Allow edit project', subtitle: 'Let viewers edit project data' },
	{
		key: 'canAddFiles',
		title: 'Allow add files',
		subtitle: 'Let viewers add files to the project',
	},
	{
		key: 'canRemoveFiles',
		title: 'Allow remove files',
		subtitle: 'Let viewers remove existing files from the project',
	},
];

const STATUS_TEXT = {
	idle: '',
	saving: 'Saving…',
	saved: 'All changes saved',
	error: 'Could not save changes. Try again.',
} as const;

export default function ShareDialog({ documentId, handleClose, type, onChange }: ShareDialogProps) {
	const share = useShareSettings({ documentId, type, onChange });
	const {
		disabled,
		shareId,
		accessType,
		requirePassword,
		permissions,
		status,
		closing,
		loaded,
	} = share;

	const [password, setPassword] = useState('');
	const [passwordTouched, setPasswordTouched] = useState(false);
	const [confirmTurnOff, setConfirmTurnOff] = useState(false);

	const shareLink = shareId ? `${frontEndLocation}${ROUTE.share.id(shareId)}` : '';
	const hasPassword = accessType === ShareAccessType.password;
	const passwordError = passwordTouched && !password ? '1 char min' : '';
	const linkOn = !!shareId;

	const submitPassword = async () => {
		setPasswordTouched(true);
		if (!password) {
			return;
		}
		if (await share.submitPassword(password)) {
			setPassword('');
			setPasswordTouched(false);
		}
	};

	const changeRequirePassword = (value: boolean) => {
		setPassword('');
		setPasswordTouched(false);
		share.changeRequirePassword(value);
	};

	const close = () => {
		if (!closing) {
			share.requestClose(handleClose);
		}
	};

	return (
		<Dialog open onClose={close} fullWidth maxWidth="xs">
			<DialogTitle
				variant="h2"
				sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
			>
				<span>Share {TITLE_MAP[type]}</span>
				<IconButton aria-label="Close" onClick={close} disabled={closing} size="small">
					<Close fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{/* Share link */}
				<Box
					sx={(theme) => ({
						display: 'flex',
						alignItems: 'center',
						gap: 2,
						mb: 3.5,
						py: 2.5,
						px: 3,
						border: `1px solid ${DESIGN_COLORS.border}`,
						borderRadius: '9px',
						backgroundColor: DESIGN_COLORS.surfaceMuted,
						fontFamily: theme.typography.fontFamilyMono,
					})}
				>
					<Typography
						title={shareLink}
						sx={{
							flex: 1,
							minWidth: 0,
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
							fontFamily: 'inherit',
							fontSize: 12.5,
							color: linkOn ? DESIGN_COLORS.muted : DESIGN_COLORS.faint,
						}}
					>
						{shareLink || 'Turn on link sharing to get a link'}
					</Typography>
					<Button
						variant="outlined"
						size="small"
						startIcon={<IconCopy />}
						disabled={disabled || !linkOn}
						onClick={() => copy(shareLink)}
						sx={{ flexShrink: 0 }}
					>
						Copy
					</Button>
				</Box>

				<Typography
					role="status"
					sx={{
						minHeight: 18,
						mb: 1,
						fontSize: 12,
						color: status === 'error' ? 'error.main' : DESIGN_COLORS.muted,
					}}
				>
					{STATUS_TEXT[status]}
				</Typography>

				<SettingRow
					title="Link sharing"
					subtitle={
						linkOn
							? 'Turn off to delete the link. It will stop working for everyone.'
							: 'Create a link to share this ' + TITLE_MAP[type]
					}
					checked={linkOn}
					disabled={disabled}
					onChange={(value) => (value ? share.enable() : setConfirmTurnOff(true))}
				/>
				<SettingRow
					title="Require password"
					subtitle={
						linkOn && requirePassword
							? 'Only people with the password can view'
							: 'Anyone with the link can view'
					}
					checked={linkOn && requirePassword}
					disabled={disabled || !linkOn}
					onChange={changeRequirePassword}
				/>

				{linkOn && requirePassword && (
					<Box sx={{ pb: 2.5 }}>
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
							<TextField
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								onBlur={() => password && setPasswordTouched(true)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										submitPassword();
									}
								}}
								placeholder={hasPassword ? 'Password is set. Enter a new one' : 'Set a password'}
								error={!!passwordError}
								disabled={disabled}
								size="small"
								fullWidth
								slotProps={{ htmlInput: { 'aria-label': 'Password' } }}
							/>
							<Button
								variant="contained"
								onClick={submitPassword}
								disabled={disabled || !password}
								// the theme sets `height: max-content` on buttons, which blocks stretching to the input height
								sx={{ flexShrink: 0, textWrap: 'nowrap', height: 'auto' }}
							>
								{hasPassword ? 'Update password' : 'Set password'}
							</Button>
						</Box>
						{(passwordError || !hasPassword) && (
							<FormHelperText error={!!passwordError} sx={{ mx: 1.75 }}>
								{passwordError || 'The link stays public until you set a password'}
							</FormHelperText>
						)}
						<Button
							variant="text"
							size="small"
							disabled={disabled}
							onClick={() => {
								setPassword(generatePassword());
								setPasswordTouched(false);
							}}
							sx={{
								mt: 0.5,
								px: 0,
								textTransform: 'none',
								textDecoration: 'underline',
								fontSize: 12,
							}}
						>
							Generate strong password
						</Button>
					</Box>
				)}

				{type === SharingType.PROJECT && (
					<>
						<Typography
							sx={{
								mt: 3,
								mb: 1,
								fontSize: 12,
								fontWeight: 700,
								letterSpacing: '0.04em',
								textTransform: 'uppercase',
								color: DESIGN_COLORS.muted,
							}}
						>
							Permissions
						</Typography>
						{PERMISSIONS.map(({ key, title, subtitle }) => (
							<SettingRow
								key={key}
								title={title}
								subtitle={subtitle}
								checked={permissions[key]}
								disabled={!loaded || closing || !linkOn}
								onChange={(value) => share.setPermission(key, value)}
							/>
						))}
					</>
				)}
			</DialogContent>
			{confirmTurnOff && (
				<ConfirmDialog
					title="Turn off link sharing?"
					description="The link will be deleted and will stop working for everyone who has it. Turning sharing on again creates a new link."
					confirmLabel="Turn off"
					confirmColor="error"
					onCancel={() => setConfirmTurnOff(false)}
					onConfirm={() => {
						setConfirmTurnOff(false);
						share.disable();
					}}
				/>
			)}
		</Dialog>
	);
}
